/**
 * FX Alerts — Comprehensive Technical analysis for all Nigastock FX pairs
 *
 * Coverage: 6 FX pairs (USD/NGN, EUR/NGN, GBP/NGN, CHF/NGN, JPY/NGN, CAD/NGN)
 * Technical Indicators: MA, RSI, MACD, Bollinger Bands, Volume, ATR, Stochastic, Williams %R, VWAP, Support/Resistance
 * Analysis Types: Trend, Momentum, Volatility, Support/Resistance, Market Sentiment
 * Timeframes: 1H, 4H, 1D
 * Severity: Critical, High, Medium, Low
 */

export type AlertType = 'trend' | 'momentum' | 'volatility' | 'support_resistance' | 'sentiment';
export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface FXAlert {
  id: string;
  pair: string;
  type: AlertType;
  severity: AlertSeverity;
  timeframe: '1H' | '4H' | '1D';
  title: string;
  description: string;
  metric?: string;
  current?: number;
  threshold?: number;
  timestamp: number;
  action?: 'BUY' | 'SELL' | 'HOLD';
}

// All supported FX pairs
const FX_PAIRS = ['USD/NGN', 'EUR/NGN', 'GBP/NGN', 'CHF/NGN', 'JPY/NGN', 'CAD/NGN'];

// Comprehensive FX Alerts Database
const fxAlertsData: FXAlert[] = [
  // USD/NGN Alerts
  {
    id: 'usdngn-trend-1h-golden',
    pair: 'USD/NGN',
    type: 'trend',
    severity: 'high',
    timeframe: '1H',
    title: 'Golden Cross on 1H',
    description: '50-hour MA crossed above 200-hour MA - strong uptrend signal',
    metric: 'MA50/MA200',
    current: 612.5,
    threshold: 610.0,
    timestamp: Date.now() - 1800000,
    action: 'BUY',
  },
  {
    id: 'usdngn-momentum-1h-rsi',
    pair: 'USD/NGN',
    type: 'momentum',
    severity: 'medium',
    timeframe: '1H',
    title: 'RSI Overbought (1H)',
    description: 'RSI above 70 indicates overbought conditions on 1H chart',
    metric: 'RSI(14)',
    current: 72,
    threshold: 70,
    timestamp: Date.now() - 1200000,
    action: 'SELL',
  },
  {
    id: 'usdngn-volatility-4h-atr',
    pair: 'USD/NGN',
    type: 'volatility',
    severity: 'medium',
    timeframe: '4H',
    title: 'ATR Expansion (4H)',
    description: 'Average True Range expanding - increased volatility expected on 4H',
    metric: 'ATR(14)',
    current: 2.8,
    threshold: 2.0,
    timestamp: Date.now() - 3600000,
  },
  {
    id: 'usdngn-support-1d-level',
    pair: 'USD/NGN',
    type: 'support_resistance',
    severity: 'critical',
    timeframe: '1D',
    title: 'Breaking Above Key Resistance (1D)',
    description: 'Price broke above 610.0 daily resistance - strong breakout signal',
    metric: 'Resistance Level',
    current: 612.5,
    threshold: 610.0,
    timestamp: Date.now() - 5400000,
    action: 'BUY',
  },
  {
    id: 'usdngn-sentiment-1d-bullish',
    pair: 'USD/NGN',
    type: 'sentiment',
    severity: 'high',
    timeframe: '1D',
    title: 'Bullish Market Sentiment',
    description: 'Strong bid on USD/NGN - Safe Haven flows into USD amid market uncertainty',
    metric: 'Sentiment',
    timestamp: Date.now() - 7200000,
    action: 'BUY',
  },

  // EUR/NGN Alerts
  {
    id: 'eurngn-trend-4h-downtrend',
    pair: 'EUR/NGN',
    type: 'trend',
    severity: 'high',
    timeframe: '4H',
    title: 'Death Cross on 4H',
    description: '50-hour MA crossed below 200-hour MA - strong downtrend signal',
    metric: 'MA50/MA200',
    current: 665.2,
    threshold: 670.0,
    timestamp: Date.now() - 2400000,
    action: 'SELL',
  },
  {
    id: 'eurngn-momentum-1h-macd',
    pair: 'EUR/NGN',
    type: 'momentum',
    severity: 'medium',
    timeframe: '1H',
    title: 'MACD Bearish Crossover (1H)',
    description: 'MACD line crossed below signal line - sell signal on 1H',
    metric: 'MACD',
    current: -1.2,
    threshold: 0.0,
    timestamp: Date.now() - 900000,
    action: 'SELL',
  },
  {
    id: 'eurngn-volatility-1d-bb',
    pair: 'EUR/NGN',
    type: 'volatility',
    severity: 'medium',
    timeframe: '1D',
    title: 'Bollinger Band Squeeze (1D)',
    description: 'Price within tight Bollinger Bands - volatility contraction on daily',
    metric: 'Bollinger Bands',
    current: 665.5,
    threshold: 665.0,
    timestamp: Date.now() - 3000000,
  },
  {
    id: 'eurngn-support-1d-level',
    pair: 'EUR/NGN',
    type: 'support_resistance',
    severity: 'high',
    timeframe: '1D',
    title: 'Testing Daily Support Level',
    description: 'Price approaching 660.0 daily support - watch for bounce/break',
    metric: 'Support Level',
    current: 665.2,
    threshold: 660.0,
    timestamp: Date.now() - 4800000,
  },
  {
    id: 'eurngn-sentiment-1d-weak',
    pair: 'EUR/NGN',
    type: 'sentiment',
    severity: 'high',
    timeframe: '1D',
    title: 'Weak EUR Sentiment',
    description: 'EUR weakness continues - hawkish Fed expectations weighing on Euro',
    metric: 'Sentiment',
    timestamp: Date.now() - 6600000,
    action: 'SELL',
  },

  // GBP/NGN Alerts
  {
    id: 'gbpngn-trend-1h-uptrend',
    pair: 'GBP/NGN',
    type: 'trend',
    severity: 'high',
    timeframe: '1H',
    title: 'Strong Uptrend (1H)',
    description: 'Price making higher highs and higher lows - sustained uptrend on 1H',
    metric: 'Trend',
    current: 782.3,
    threshold: 780.0,
    timestamp: Date.now() - 1500000,
    action: 'BUY',
  },
  {
    id: 'gbpngn-momentum-4h-stoch',
    pair: 'GBP/NGN',
    type: 'momentum',
    severity: 'medium',
    timeframe: '4H',
    title: 'Stochastic Oversold (4H)',
    description: 'Stochastic below 20 on 4H - potential bounce setup',
    metric: 'Stochastic',
    current: 18,
    threshold: 20,
    timestamp: Date.now() - 2100000,
    action: 'BUY',
  },
  {
    id: 'gbpngn-volatility-1d-atr',
    pair: 'GBP/NGN',
    type: 'volatility',
    severity: 'low',
    timeframe: '1D',
    title: 'Normal Volatility (1D)',
    description: 'ATR in normal range - no extreme volatility on daily timeframe',
    metric: 'ATR(14)',
    current: 1.8,
    threshold: 2.0,
    timestamp: Date.now() - 3300000,
  },
  {
    id: 'gbpngn-support-4h-level',
    pair: 'GBP/NGN',
    type: 'support_resistance',
    severity: 'medium',
    timeframe: '4H',
    title: 'Bounce from 4H Support',
    description: 'Price bounced from 780.0 4H support - uptrend continuation likely',
    metric: 'Support Level',
    current: 782.3,
    threshold: 780.0,
    timestamp: Date.now() - 4500000,
    action: 'BUY',
  },
  {
    id: 'gbpngn-sentiment-1d-strong',
    pair: 'GBP/NGN',
    type: 'sentiment',
    severity: 'high',
    timeframe: '1D',
    title: 'Strong GBP Sentiment',
    description: 'GBP strengthening - BOE hawkish stance supporting sterling',
    metric: 'Sentiment',
    timestamp: Date.now() - 6000000,
    action: 'BUY',
  },

  // CHF/NGN Alerts
  {
    id: 'chfngn-trend-1d-sideways',
    pair: 'CHF/NGN',
    type: 'trend',
    severity: 'low',
    timeframe: '1D',
    title: 'Consolidation Pattern (1D)',
    description: 'Price in consolidation range on daily - waiting for directional breakout',
    metric: 'Trend',
    current: 689.5,
    threshold: 690.0,
    timestamp: Date.now() - 2700000,
    action: 'HOLD',
  },
  {
    id: 'chfngn-momentum-1h-rsi',
    pair: 'CHF/NGN',
    type: 'momentum',
    severity: 'medium',
    timeframe: '1H',
    title: 'RSI Neutral Zone (1H)',
    description: 'RSI in 40-60 range on 1H - no overbought/oversold condition',
    metric: 'RSI(14)',
    current: 52,
    threshold: 50,
    timestamp: Date.now() - 1800000,
  },
  {
    id: 'chfngn-volatility-4h-contraction',
    pair: 'CHF/NGN',
    type: 'volatility',
    severity: 'medium',
    timeframe: '4H',
    title: 'Volatility Contraction (4H)',
    description: 'Lower ATR on 4H - prepare for potential breakout',
    metric: 'ATR(14)',
    current: 1.5,
    threshold: 1.8,
    timestamp: Date.now() - 3600000,
  },
  {
    id: 'chfngn-support-1d-double',
    pair: 'CHF/NGN',
    type: 'support_resistance',
    severity: 'medium',
    timeframe: '1D',
    title: 'Double Bottom Pattern (1D)',
    description: 'Double bottom formation at 685.0 on daily - bullish reversal setup',
    metric: 'Support Level',
    current: 689.5,
    threshold: 685.0,
    timestamp: Date.now() - 5100000,
    action: 'BUY',
  },
  {
    id: 'chfngn-sentiment-1d-neutral',
    pair: 'CHF/NGN',
    type: 'sentiment',
    severity: 'low',
    timeframe: '1D',
    title: 'Neutral CHF Sentiment',
    description: 'CHF trading flat - waiting for risk sentiment directional clue',
    metric: 'Sentiment',
    timestamp: Date.now() - 6300000,
    action: 'HOLD',
  },

  // JPY/NGN Alerts
  {
    id: 'jpyngn-trend-4h-uptrend',
    pair: 'JPY/NGN',
    type: 'trend',
    severity: 'high',
    timeframe: '4H',
    title: 'Strong Uptrend (4H)',
    description: 'Series of higher lows forming on 4H - sustained uptrend intact',
    metric: 'Trend',
    current: 5.82,
    threshold: 5.75,
    timestamp: Date.now() - 2250000,
    action: 'BUY',
  },
  {
    id: 'jpyngn-momentum-1h-macd',
    pair: 'JPY/NGN',
    type: 'momentum',
    severity: 'high',
    timeframe: '1H',
    title: 'MACD Bullish Momentum (1H)',
    description: 'Histogram bars growing above zero on 1H - strong momentum',
    metric: 'MACD',
    current: 0.85,
    threshold: 0.0,
    timestamp: Date.now() - 1350000,
    action: 'BUY',
  },
  {
    id: 'jpyngn-volatility-1d-bb-break',
    pair: 'JPY/NGN',
    type: 'volatility',
    severity: 'high',
    timeframe: '1D',
    title: 'Bollinger Band Breakout (1D)',
    description: 'Price broke above upper Bollinger Band - expansion move starting',
    metric: 'Bollinger Bands',
    current: 5.85,
    threshold: 5.78,
    timestamp: Date.now() - 3450000,
    action: 'BUY',
  },
  {
    id: 'jpyngn-support-4h-level',
    pair: 'JPY/NGN',
    type: 'support_resistance',
    severity: 'medium',
    timeframe: '4H',
    title: 'Holding Above Key Support (4H)',
    description: 'Price holding above 5.75 4H support - uptrend continuation likely',
    metric: 'Support Level',
    current: 5.82,
    threshold: 5.75,
    timestamp: Date.now() - 4650000,
  },
  {
    id: 'jpyngn-sentiment-1d-risk-on',
    pair: 'JPY/NGN',
    type: 'sentiment',
    severity: 'high',
    timeframe: '1D',
    title: 'Risk-On Sentiment',
    description: 'Risk appetite increasing - JPY weakness as carry funding currency',
    metric: 'Sentiment',
    timestamp: Date.now() - 6150000,
    action: 'BUY',
  },

  // CAD/NGN Alerts
  {
    id: 'cadngn-trend-1h-recovery',
    pair: 'CAD/NGN',
    type: 'trend',
    severity: 'medium',
    timeframe: '1H',
    title: 'Recovery Rally (1H)',
    description: 'Price recovering from 1H lows - potential rebound setup',
    metric: 'Trend',
    current: 475.8,
    threshold: 475.0,
    timestamp: Date.now() - 1950000,
    action: 'BUY',
  },
  {
    id: 'cadngn-momentum-4h-rsi',
    pair: 'CAD/NGN',
    type: 'momentum',
    severity: 'medium',
    timeframe: '4H',
    title: 'RSI Oversold Bounce (4H)',
    description: 'RSI below 30 on 4H - oversold condition presenting bounce opportunity',
    metric: 'RSI(14)',
    current: 28,
    threshold: 30,
    timestamp: Date.now() - 2550000,
    action: 'BUY',
  },
  {
    id: 'cadngn-volatility-1d-normal',
    pair: 'CAD/NGN',
    type: 'volatility',
    severity: 'low',
    timeframe: '1D',
    title: 'Normal Volatility (1D)',
    description: 'ATR in typical range - standard market volatility on daily',
    metric: 'ATR(14)',
    current: 1.6,
    threshold: 1.8,
    timestamp: Date.now() - 3750000,
  },
  {
    id: 'cadngn-support-1d-break',
    pair: 'CAD/NGN',
    type: 'support_resistance',
    severity: 'high',
    timeframe: '1D',
    title: 'Support Break on Daily',
    description: 'Price broke below 475.0 daily support - watch for further weakness',
    metric: 'Support Level',
    current: 475.8,
    threshold: 475.0,
    timestamp: Date.now() - 4950000,
    action: 'SELL',
  },
  {
    id: 'cadngn-sentiment-1d-weak',
    pair: 'CAD/NGN',
    type: 'sentiment',
    severity: 'medium',
    timeframe: '1D',
    title: 'Weak CAD Sentiment',
    description: 'CAD weakness persisting - Oil prices not supporting commodity currency',
    metric: 'Sentiment',
    timestamp: Date.now() - 6450000,
    action: 'SELL',
  },
];

// Helper Functions
export const getAllFXAlerts = (): FXAlert[] => fxAlertsData;

export const getAlertsForPair = (pair: string): FXAlert[] => {
  return fxAlertsData.filter(a => a.pair === pair);
};

export const getAlertsByType = (type: AlertType): FXAlert[] => {
  return fxAlertsData.filter(a => a.type === type);
};

export const getAlertsBySeverity = (severity: AlertSeverity): FXAlert[] => {
  return fxAlertsData.filter(a => a.severity === severity);
};

export const getAlertsByTimeframe = (timeframe: '1H' | '4H' | '1D'): FXAlert[] => {
  return fxAlertsData.filter(a => a.timeframe === timeframe);
};

export const getCriticalAlerts = (): FXAlert[] => {
  return getAlertsBySeverity('critical');
};

export const getHighSeverityAlerts = (): FXAlert[] => {
  return getAlertsBySeverity('high');
};

export const getAllPairs = (): string[] => FX_PAIRS;

export const countAlertsByPair = (pair: string): { total: number; critical: number; high: number; medium: number; low: number } => {
  const all = getAlertsForPair(pair);
  return {
    total: all.length,
    critical: all.filter(a => a.severity === 'critical').length,
    high: all.filter(a => a.severity === 'high').length,
    medium: all.filter(a => a.severity === 'medium').length,
    low: all.filter(a => a.severity === 'low').length,
  };
};

export const countTotalAlerts = (): { total: number; critical: number; high: number; medium: number; low: number } => {
  return {
    total: fxAlertsData.length,
    critical: getCriticalAlerts().length,
    high: getHighSeverityAlerts().length,
    medium: getAlertsBySeverity('medium').length,
    low: getAlertsBySeverity('low').length,
  };
};
